import React, { useEffect, useRef, useState } from 'react';
import { MessageSquare, Phone, Send, X } from 'lucide-react';
import { site } from '../data/site';
type Message = {
  id: number;
  from: 'them' | 'you';
  text: string;
};
const quickPrompts = ['Install cabinet hardware', 'Mount a TV or mirror', 'Wi-Fi lock or keypad', 'Get a flat quote'];
const openingMessage: Message = {
  id: 0,
  from: 'them',
  text: `Welcome to ${site.name}. Tell us what needs installing and we'll come back with availability and a flat quote — usually within the hour during shop hours.`
};
export function ChatWidget() {
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState<Message[]>([openingMessage]);
  const [draft, setDraft] = useState('');
  const [typing, setTyping] = useState(false);
  const listRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const timers = useRef<number[]>([]);
  useEffect(() => {
    return () => timers.current.forEach(t => window.clearTimeout(t));
  }, []);
  useEffect(() => {
    if (open) inputRef.current?.focus();
  }, [open]);
  useEffect(() => {
    if (listRef.current) listRef.current.scrollTop = listRef.current.scrollHeight;
  }, [messages, typing]);
  useEffect(() => {
    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setOpen(false);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);
  const send = (text: string) => {
    const value = text.trim();
    if (!value) return;
    setMessages(prev => [...prev, {
      id: Date.now(),
      from: 'you',
      text: value
    }]);
    setDraft('');
    setTyping(true);
    const timer = window.setTimeout(() => {
      setTyping(false);
      setMessages(prev => [...prev, {
        id: Date.now() + 1,
        from: 'them',
        text: `Noted — thank you. Send your address and a rough piece count and we'll confirm a window. Prefer to talk it through? Call ${site.phone}.`
      }]);
    }, 1100);
    timers.current.push(timer);
  };
  return <>
      {open && <div role="dialog" aria-label={`Chat with ${site.name}`} className="fixed bottom-24 right-4 z-[60] flex h-[520px] w-[min(22rem,calc(100vw-2rem))] flex-col border border-ink-600 bg-ink text-ivory shadow-2xl sm:right-6" data-id="element-1" data-mp="rtdrwxpnf17v">
          <div className="flex items-start justify-between gap-4 border-b border-ink-600 bg-ink-800 px-5 py-4" data-id="element-2" data-mp="1esjqxidkbzyz">
            <div data-id="element-3" data-mp="1arq4ktfrqt87">
              <p className="font-display text-lg leading-tight" data-id="element-4" data-mp="1aq5b7756xgbx">Concierge</p>
              <p className="mt-1 flex items-center gap-2 font-mono text-[10px] uppercase tracking-[0.18em] text-ivory/45" data-id="element-5" data-mp="1oj4kusiakgbk">
                <span className="h-1.5 w-1.5 rounded-full bg-brass-light" aria-hidden="true" data-id="element-6" data-mp="1jktcynbk4fvz" />
                Replies in about an hour
              </p>
            </div>
            <button type="button" onClick={() => setOpen(false)} className="text-ivory/50 transition-colors hover:text-ivory" aria-label="Close chat" data-id="element-7" data-mp="1ti7tso1e76ot">
              <X className="h-5 w-5" data-id="element-8" data-mp="2h41njk06ar1k" />
            </button>
          </div>

          <div ref={listRef} className="flex-1 space-y-4 overflow-y-auto px-5 py-5" aria-live="polite" data-id="element-9" data-mp="235k1rrttvoma">
            {messages.map(message => <div key={message.id} className={`max-w-[85%] px-4 py-3 text-sm leading-relaxed ${message.from === 'you' ? 'ml-auto bg-brass text-ink' : 'border border-ink-600 bg-ink-800 text-ivory/80'}`} data-id="element-10" data-mp="1tfqs4wd8xjko">
                {message.text}
              </div>)}
            {typing && <p className="font-mono text-[10px] uppercase tracking-[0.18em] text-ivory/40" data-id="element-11" data-mp="1agcwm9zvc0s2">
                Typing…
              </p>}
          </div>

          {messages.length === 1 && <div className="flex flex-wrap gap-2 px-5 pb-3" data-id="element-12" data-mp="2iplhcis2eb5i">
              {quickPrompts.map(prompt => <button key={prompt} type="button" onClick={() => send(prompt)} className="border border-ivory/20 px-3 py-2 font-mono text-[10px] uppercase tracking-[0.14em] text-ivory/60 transition-colors hover:border-brass hover:text-brass-light" data-id="element-13" data-mp="2dsv3m23qfrn9">
                  {prompt}
                </button>)}
            </div>}

          <form onSubmit={event => {
        event.preventDefault();
        send(draft);
      }} className="flex items-center gap-2 border-t border-ink-600 bg-ink-800 px-3 py-3" data-id="element-14" data-mp="1v356v9gesvsp">
            <label htmlFor="chat-message" className="sr-only" data-id="element-15" data-mp="3kgig9d3dpa0o">
              Message
            </label>
            <input id="chat-message" ref={inputRef} value={draft} onChange={event => setDraft(event.target.value)} placeholder="Type a message…" className="h-10 flex-1 bg-transparent px-2 text-sm text-ivory outline-none placeholder:text-ivory/35" data-id="element-16" data-mp="3fgmd1g2i9mm6" />
            <button type="submit" disabled={!draft.trim()} className="flex h-10 w-10 items-center justify-center bg-brass text-ink transition-colors hover:bg-brass-light disabled:opacity-40" aria-label="Send message" data-id="element-17" data-mp="llx8xz9c99e">
              <Send className="h-4 w-4" data-id="element-18" data-mp="3915mzn0pz9jp" />
            </button>
          </form>

          <a href={`tel:${site.phone.replace(/[^0-9]/g, '')}`} className="flex items-center justify-center gap-2 border-t border-ink-600 py-3 font-mono text-[10px] uppercase tracking-[0.18em] text-ivory/45 transition-colors hover:text-brass-light" data-id="element-19" data-mp="3dyp6b1c9ycii">
            <Phone className="h-3.5 w-3.5" aria-hidden="true" data-id="element-20" data-mp="y7t30b7as7nc" />
            Or call {site.phone}
          </a>
        </div>}

      <button type="button" onClick={() => setOpen(v => !v)} aria-expanded={open} aria-label={open ? 'Close chat' : 'Chat with us'} className="fixed bottom-5 right-4 z-[60] flex h-14 w-14 items-center justify-center rounded-full bg-brass text-ink shadow-xl transition-transform hover:scale-105 hover:bg-brass-light focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brass sm:right-6" data-id="element-21" data-mp="2ua5yug0t8q73">
        {open ? <X className="h-6 w-6" data-id="element-22" data-mp="26ng1hrzepjst" /> : <MessageSquare className="h-6 w-6" data-id="element-23" data-mp="26k1x6e76n0iq" />}
      </button>
    </>;
}